import { useState, useEffect, useRef } from 'react';
import { Plus, ChevronRight, ChevronLeft, Edit3, X, Save, Trash2, Upload, Image as ImageIcon } from 'lucide-react';
import { LicensesView } from './LicensesView.jsx';
import { CompanyAppointmentsView } from './CompanyAppointmentsView.jsx';
import { uploadAgencyLogo, deleteAgencyLogo, updateAgencyName, getAgencyTimezone, updateAgencyTimezone } from '../api.js';
import { confirmDialog } from './ui.jsx';
import { BrandFontEditor } from './BrandFontEditor.jsx';

export function AgencyView({ clients, policies, leads, commissions, expenses, tasks, users, onUsersChange, currentUser, lang, licenses, onLicensesChange, compAppts, onAppointmentsChange, userPrefs, onSetPref }) {
  const es = lang === "es";
  // API/production mode → the "Agregar Usuario" button below is local-only
  // (legacy demo path: builds a phantom user that never persists and can't log
  // in). Hide it in API mode; real teammates are added from the Team module via
  // "Invitar". Local/demo mode keeps the button so the seeded demo still works.
  const isApiMode = typeof window !== "undefined" && !!window.__deskteAuth;
  // Whole-calendar-day diff in LOCAL time. Raw `new Date('YYYY-MM-DD')` parses
  // as UTC midnight, so subtracting the local `new Date()` was off by the UTC
  // offset (Florida -4/-5h) and could flag a license expiring a day early/late
  // near the 30/60-day thresholds (feedback_local_day_not_utc).
  const daysUntil = (d) => {
    if (!d) return null;
    const target = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + 'T00:00:00') : new Date(d);
    if (isNaN(target.getTime())) return null;
    const now = new Date();
    const a = new Date(target.getFullYear(), target.getMonth(), target.getDate());
    const b = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return Math.round((a - b) / 86400000);
  };
  const [tab, setTab] = useState("leaderboard");
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [editingAgent, setEditingAgent] = useState(null);
  const [agentProfileTab, setAgentProfileTab] = useState("info");
  const [addModal, setAddModal] = useState(false);
  const [newAgent, setNewAgent] = useState({ name: "", role: "agent", pin: "0000", color: "#06d6a0", parentAgentId: null });

  const agents = (users || []).filter(u => ["agency_owner", "agency_admin", "agent"].includes(u.role));
  const assistants = (users || []).filter(u => u.role === "assistant");
  const allPolicies = policies || []; const allClients = clients || []; const allLeads = leads || [];
  const allComm = commissions || []; const allExp = expenses || [];

  // Per-agent stats
  const agentStats = agents.map(agent => {
    const agClients = allClients.length; // In prod: filter by agent_id
    const agPolicies = allPolicies.filter(p => p.status === "active").length;
    const agPremium = allPolicies.filter(p => p.status === "active").reduce((s, p) => s + (parseFloat(p.monthlyPremium) || 0), 0);
    const agLeads = allLeads.filter(l => l.assignedTo === agent.name || l.assignedTo === "Agente").length;
    const agConverted = allLeads.filter(l => (l.assignedTo === agent.name || l.assignedTo === "Agente") && l.status === "converted").length;
    const agComm = allComm.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0) / Math.max(agents.length, 1);
    const agAssistants = assistants.filter(a => a.parentAgentId === agent.id).length;
    return { ...agent, clients: agClients, policies: agPolicies, premium: agPremium, leads: agLeads, converted: agConverted, commissions: agComm, assistantCount: agAssistants };
  }).sort((a, b) => b.premium - a.premium);

  const totalPremium = allPolicies.filter(p => p.status === "active").reduce((s, p) => s + (parseFloat(p.monthlyPremium) || 0), 0);
  const totalComm = allComm.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
  const totalExp = allExp.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);

  // (Removed) Hardcoded PLAN_PRICES / monthlyBill — they were placeholder numbers
  // that didn't match Deskte's real pricing and weren't wired to Stripe. The
  // "Costo Deskte/Mes" card + Billing tab that used them were removed. Re-introduce
  // real values from the billing backend when live billing ships.

  const addUser = () => {
    if (!newAgent.name) return;
    const initials = newAgent.name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
    onUsersChange(prev => [...prev, { ...newAgent, id: "u_" + Date.now(), initials, agencyId: currentUser.agencyId }]);
    setAddModal(false); setNewAgent({ name: "", role: "agent", pin: "0000", color: "#06d6a0", parentAgentId: null });
  };

  const removeUser = async (id) => { if (id === currentUser.id) return; if (await confirmDialog({ message: es ? "¿Eliminar este usuario?" : "Remove this user?", danger: true })) onUsersChange(prev => prev.filter(u => u.id !== id)); };

  const medals = ["", "", ""];

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem", flexWrap: "wrap", gap: ".5rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
          <span style={{ fontSize: "1.3rem" }}></span>
          <h2 style={{ fontSize: "1.1rem", fontWeight: 800, color: "var(--c-white,#eef6ff)", margin: 0 }}>{es ? "Mi Agencia" : "My Agency"}</h2>
        </div>
        {!isApiMode && (
          <button type="button" onClick={() => setAddModal(true)}
            style={{ display: "flex", alignItems: "center", gap: 5, background: "linear-gradient(135deg,#0099cc,#0055aa)", border: "none", color: "#fff", fontSize: ".78rem", fontWeight: 700, padding: ".5rem 1rem", borderRadius: 9, cursor: "pointer", fontFamily: "inherit" }}>
            <Plus size={14} />{es ? "Agregar Usuario" : "Add User"}
          </button>
        )}
      </div>

      {/* Agency KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: ".5rem", marginBottom: "1rem" }}>
        {[
          { icon: "", val: agents.length, lbl: es ? "Agentes" : "Agents", color: "var(--c-cyan,#00d4ff)", sub: `${assistants.length} ${es ? "asientos" : "seats"}` },
          { icon: "", val: allPolicies.filter(p => p.status === "active").length, lbl: es ? "Pólizas Activas" : "Active Policies", color: "var(--c-green,#00ff88)" },
          { icon: "", val: `$${totalPremium.toLocaleString("en-US",{maximumFractionDigits:0})}`, lbl: es ? "Premium/Mes" : "Premium/Mo", color: "var(--c-cyan,#00d4ff)", sub: `$${(totalPremium * 12).toLocaleString("en-US",{maximumFractionDigits:0})}/yr` },
          { icon: "", val: `$${totalComm.toLocaleString("en-US",{maximumFractionDigits:0})}`, lbl: es ? "Comisiones" : "Commissions", color: "var(--c-green,#00ff88)" },
          { icon: "", val: `$${totalExp.toLocaleString("en-US",{maximumFractionDigits:0})}`, lbl: es ? "Gastos" : "Expenses", color: "var(--c-red,#ff3d6b)" },
          // "Costo Deskte/Mes" card removed — pricing isn't wired to real billing
          // (Stripe) yet, so it showed placeholder numbers. Re-add when billing is live.
        ].map((k, i) => (
          <div key={i} style={{ background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 12, padding: ".75rem .875rem" }}>
            <div style={{ fontSize: ".95rem", marginBottom: ".2rem" }}>{k.icon}</div>
            <div style={{ fontFamily: "var(--font-display)", fontSize: "1.3rem", fontWeight: 800, color: k.color }}>{k.val}</div>
            <div style={{ fontSize: ".6rem", color: "var(--c-text3,#45688a)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".08em", fontFamily: "var(--font-mono)" }}>{k.lbl}</div>
            {k.sub && <div style={{ fontSize: ".58rem", color: "var(--c-text2,#7aa3c0)", marginTop: 2 }}>{k.sub}</div>}
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: ".35rem", marginBottom: "1rem", flexWrap: "wrap" }}>
        {[["leaderboard", "Leaderboard"], ["agents", `${es ? "Agentes CRM" : "Agents CRM"}`], ["team", `${es ? "Equipo" : "Team"} (${users.length})`], ["branding", `${es ? "Marca" : "Branding"}`]].map(([id, label]) => (
          <button type="button" key={id} onClick={() => setTab(id)}
            style={{ padding: ".4rem .85rem", borderRadius: 8, border: `1px solid ${tab === id ? "rgba(0,212,255,.4)" : "var(--c-border,#1a3550)"}`, background: tab === id ? "var(--c-cyan-dim)" : "transparent", color: tab === id ? "var(--c-cyan,#00d4ff)" : "var(--c-text3,#45688a)", fontSize: ".75rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            {label}
          </button>
        ))}
      </div>

      {/* LEADERBOARD */}
      {tab === "leaderboard" && (
        <div style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
          {agentStats.map((agent, i) => (
            <div key={agent.id} style={{ background: i === 0 ? "rgba(0,212,255,.06)" : "var(--c-surface2,#112538)", border: `1px solid ${i === 0 ? "rgba(0,212,255,.25)" : "var(--c-border,#1a3550)"}`, borderLeft: `4px solid ${i === 0 ? "var(--c-cyan,#00d4ff)" : i === 1 ? "var(--c-green,#00ff88)" : i === 2 ? "var(--c-amber,#ffb700)" : "var(--c-border,#1a3550)"}`, borderRadius: 12, padding: "1rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: ".75rem", marginBottom: ".65rem" }}>
                <div style={{ fontSize: "1.5rem", width: 36, textAlign: "center" }}>{medals[i] || `#${i + 1}`}</div>
                <div style={{ width: 42, height: 42, borderRadius: "50%", background: agent.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: ".85rem", fontWeight: 700, color: "#000", border: "2px solid var(--c-border,#1a3550)" }}>{agent.initials}</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: ".9rem", color: "var(--c-white,#eef6ff)" }}>{agent.name}</div>
                  <div style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontFamily: "var(--font-mono)", textTransform: "uppercase" }}>
                    {({ agency_owner: "Agency Owner", agency_admin: "Admin", agent: "Agent" })[agent.role] || agent.role}
                    {agent.assistantCount > 0 && ` · ${agent.assistantCount} ${es ? "asiento(s)" : "seat(s)"}`}
                  </div>
                </div>
                <div style={{ marginLeft: "auto", textAlign: "right" }}>
                  <div style={{ fontFamily: "var(--font-display)", fontSize: "1.3rem", fontWeight: 800, color: "var(--c-cyan,#00d4ff)" }}>${agent.premium.toLocaleString("en-US",{maximumFractionDigits:0})}<span style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)" }}>/mo</span></div>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: ".35rem" }}>
                {[
                  { l: es ? "Clientes" : "Clients", v: agent.clients, c: "var(--c-cyan,#00d4ff)" },
                  { l: es ? "Pólizas" : "Policies", v: agent.policies, c: "var(--c-green,#00ff88)" },
                  { l: "Leads", v: agent.leads, c: "#f472b6" },
                  { l: es ? "Convertidos" : "Converted", v: agent.converted, c: "var(--c-amber,#ffb700)" },
                  { l: es ? "Comisiones" : "Commissions", v: `$${agent.commissions.toLocaleString("en-US",{maximumFractionDigits:0})}`, c: "var(--c-green,#00ff88)" },
                ].map((s, si) => (
                  <div key={si} style={{ background: "var(--c-surface3,#162d44)", borderRadius: 7, padding: ".3rem .4rem", textAlign: "center" }}>
                    <div style={{ fontFamily: "var(--font-display)", fontSize: ".9rem", fontWeight: 800, color: s.c }}>{s.v}</div>
                    <div style={{ fontSize: ".52rem", color: "var(--c-text3,#45688a)", fontFamily: "var(--font-mono)", textTransform: "uppercase" }}>{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* AGENTS CRM */}
      {tab === "agents" && !selectedAgent && (
        <div style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
          {agents.map(agent => {
            const agLicenses = (licenses || []).filter(l => l.userId === agent.id);
            const agApptCount = Object.keys(compAppts || {}).filter(k => k.startsWith(agent.id + "::") && compAppts[k]).length;
            const agAssistants = assistants.filter(a => a.parentAgentId === agent.id);
            const age = agent.dob ? Math.floor((new Date() - new Date(agent.dob)) / 31557600000) : null;
            return (
              <div key={agent.id} onClick={() => { setSelectedAgent(agent.id); setAgentProfileTab("info"); }}
                style={{ background: "var(--c-surface2,#112538)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 12, padding: "1rem", cursor: "pointer", transition: "border-color .2s" }}
                onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(0,212,255,.4)"}
                onMouseLeave={e => e.currentTarget.style.borderColor = "var(--c-border,#1a3550)"}>
                <div style={{ display: "flex", alignItems: "center", gap: ".75rem", marginBottom: ".5rem" }}>
                  <div style={{ width: 48, height: 48, borderRadius: "50%", background: agent.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: ".95rem", fontWeight: 700, color: "#000", border: "2px solid var(--c-border,#1a3550)", flexShrink: 0 }}>{agent.initials}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: ".9rem", color: "var(--c-white,#eef6ff)" }}>{agent.name}</div>
                    <div style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", display: "flex", gap: ".5rem", flexWrap: "wrap" }}>
                      <span>{agent.email}</span>
                      {agent.phone && <span>{agent.phone}</span>}
                      {age && <span>{age} {es ? "años" : "yrs"}</span>}
                    </div>
                  </div>
                  <ChevronRight size={16} style={{ color: "var(--c-text3,#45688a)" }} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: ".35rem" }}>
                  {[
                    { l: es ? "Licencias" : "Licenses", v: agLicenses.length, c: "var(--c-cyan,#00d4ff)" },
                    { l: "Appointments", v: agApptCount, c: "var(--c-green,#00ff88)" },
                    { l: es ? "Asientos" : "Seats", v: agAssistants.length, c: "var(--c-amber,#ffb700)" },
                    { l: es ? "Estado" : "Status", v: agLicenses.some(l => { const d = daysUntil(l.expiryDate); return d !== null && d <= 30; }) ? (es ? "Alerta" : "Alert") : "OK", c: agLicenses.some(l => { const d = daysUntil(l.expiryDate); return d !== null && d <= 30; }) ? "#ef4444" : "var(--c-green,#00ff88)" },
                  ].map((s, si) => (
                    <div key={si} style={{ background: "var(--c-surface3,#162d44)", borderRadius: 7, padding: ".35rem .4rem", textAlign: "center" }}>
                      <div style={{ fontFamily: "var(--font-display)", fontSize: ".85rem", fontWeight: 800, color: s.c }}>{s.v}</div>
                      <div style={{ fontSize: ".48rem", color: "var(--c-text3,#45688a)", fontFamily: "var(--font-mono)", textTransform: "uppercase" }}>{s.l}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* AGENT PROFILE DETAIL */}
      {tab === "agents" && selectedAgent && (() => {
        const agent = users.find(u => u.id === selectedAgent);
        if (!agent) return null;
        const agLicenses = (licenses || []).filter(l => l.userId === agent.id);
        const agAssistants = assistants.filter(a => a.parentAgentId === agent.id);
        const age = agent.dob ? Math.floor((new Date() - new Date(agent.dob)) / 31557600000) : null;
        const agApptKeys = Object.keys(compAppts || {}).filter(k => k.startsWith(agent.id + "::") && compAppts[k]);

        return (
          <div>
            <button type="button" onClick={() => setSelectedAgent(null)}
              style={{ display: "flex", alignItems: "center", gap: 4, background: "none", border: "none", color: "var(--c-cyan,#00d4ff)", fontSize: ".75rem", fontWeight: 700, cursor: "pointer", marginBottom: ".75rem", fontFamily: "inherit", padding: 0 }}>
              <ChevronLeft size={14} /> {es ? "Volver a Agentes" : "Back to Agents"}
            </button>

            {/* Agent Header Card */}
            <div style={{ background: "var(--c-surface2,#112538)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 14, padding: "1.25rem", marginBottom: "1rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: ".75rem" }}>
                <div style={{ width: 60, height: 60, borderRadius: "50%", background: agent.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem", fontWeight: 700, color: "#000", border: "3px solid var(--c-border,#1a3550)", flexShrink: 0 }}>{agent.initials}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 800, fontSize: "1.1rem", color: "var(--c-white,#eef6ff)", marginBottom: 2 }}>{agent.name}</div>
                  <div style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)", fontFamily: "var(--font-mono)" }}>
                    {({ agency_owner: "Agency Owner", agency_admin: "Admin", agent: "Agent" })[agent.role] || agent.role}
                    {agAssistants.length > 0 && ` · ${agAssistants.length} ${es ? "asiento(s)" : "seat(s)"}`}
                  </div>
                </div>
                <button type="button" onClick={() => setEditingAgent({ ...agent })}
                  style={{ display: "flex", alignItems: "center", gap: 4, background: "var(--c-surface3,#162d44)", border: "1px solid var(--c-border,#1a3550)", color: "var(--c-text2,#7aa3c0)", fontSize: ".7rem", fontWeight: 600, padding: ".4rem .7rem", borderRadius: 8, cursor: "pointer", fontFamily: "inherit" }}>
                  <Edit3 size={12} /> {es ? "Editar" : "Edit"}
                </button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: ".5rem" }}>
                {[
                  { icon: "", label: "Email", value: agent.email },
                  { icon: "", label: es ? "Teléfono" : "Phone", value: agent.phone || "—" },
                  { icon: "", label: es ? "Fecha de Nac." : "Date of Birth", value: agent.dob ? `${agent.dob} (${age} ${es ? "años" : "yrs"})` : "—" },
                  { icon: "", label: es ? "Dirección" : "Address", value: agent.address || "—" },
                  { icon: "", label: es ? "Fecha Ingreso" : "Hire Date", value: agent.hireDate || "—" },
                  { icon: "", label: "PIN", value: agent.pin },
                ].map((f, i) => (
                  <div key={i} style={{ background: "var(--c-surface3,#162d44)", borderRadius: 8, padding: ".5rem .65rem" }}>
                    <div style={{ fontSize: ".5rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 2 }}>{f.icon} {f.label}</div>
                    <div style={{ fontSize: ".72rem", color: "var(--c-white,#eef6ff)", fontWeight: 600, wordBreak: "break-word" }}>{f.value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sub-tabs: Info / Licenses / Appointments */}
            <div style={{ display: "flex", gap: ".3rem", marginBottom: ".75rem" }}>
              {[
                ["info", `${es ? "Resumen" : "Summary"}`],
                ["licenses", `${es ? "Licencias" : "Licenses"} (${agLicenses.length})`],
                ["appointments", `Appointments (${agApptKeys.length})`],
              ].map(([id, label]) => (
                <button type="button" key={id} onClick={() => setAgentProfileTab(id)}
                  style={{ padding: ".35rem .75rem", borderRadius: 8, border: `1px solid ${agentProfileTab === id ? "rgba(0,212,255,.4)" : "var(--c-border,#1a3550)"}`, background: agentProfileTab === id ? "var(--c-cyan-dim)" : "transparent", color: agentProfileTab === id ? "var(--c-cyan,#00d4ff)" : "var(--c-text3,#45688a)", fontSize: ".7rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                  {label}
                </button>
              ))}
            </div>

            {/* Summary sub-tab */}
            {agentProfileTab === "info" && (
              <div style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
                {agAssistants.length > 0 && (
                  <div style={{ background: "var(--c-surface2,#112538)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 10, padding: ".75rem" }}>
                    <div style={{ fontSize: ".65rem", fontWeight: 700, color: "var(--c-text3,#45688a)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: ".4rem" }}>{es ? "Asientos" : "Seats"}</div>
                    {agAssistants.map(a => (
                      <div key={a.id} style={{ display: "flex", alignItems: "center", gap: ".5rem", padding: ".35rem 0" }}>
                        <div style={{ width: 28, height: 28, borderRadius: "50%", background: a.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: ".6rem", fontWeight: 700, color: "#000" }}>{a.initials}</div>
                        <div>
                          <div style={{ fontSize: ".75rem", fontWeight: 600, color: "var(--c-white,#eef6ff)" }}>{a.name}</div>
                          <div style={{ fontSize: ".6rem", color: "var(--c-text3,#45688a)" }}>{a.email} {a.phone ? `· ${a.phone}` : ""}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".5rem" }}>
                  <div style={{ background: "var(--c-surface2,#112538)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 10, padding: ".75rem", textAlign: "center" }}>
                    <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "var(--c-cyan,#00d4ff)", fontFamily: "var(--font-display)" }}>{agLicenses.length}</div>
                    <div style={{ fontSize: ".6rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase" }}>{es ? "Licencias" : "Licenses"}</div>
                    {agLicenses.some(l => { const d = daysUntil(l.expiryDate); return d !== null && d <= 60; }) && (
                      <div style={{ fontSize: ".55rem", color: "#ef4444", marginTop: 3, fontWeight: 700 }}>{es ? "Expirando pronto" : "Expiring soon"}</div>
                    )}
                  </div>
                  <div style={{ background: "var(--c-surface2,#112538)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 10, padding: ".75rem", textAlign: "center" }}>
                    <div style={{ fontSize: "1.5rem", fontWeight: 800, color: "var(--c-green,#00ff88)", fontFamily: "var(--font-display)" }}>{agApptKeys.length}</div>
                    <div style={{ fontSize: ".6rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase" }}>Appointments</div>
                  </div>
                </div>
              </div>
            )}

            {/* Licenses sub-tab — full LicensesView scoped to this agent */}
            {agentProfileTab === "licenses" && (
              <LicensesView licenses={licenses} onLicensesChange={onLicensesChange} currentUser={currentUser} lang={lang} userId={agent.id} />
            )}

            {/* Appointments sub-tab — full CompanyAppointmentsView scoped to this agent */}
            {agentProfileTab === "appointments" && (
              <CompanyAppointmentsView appointments={compAppts} onAppointmentsChange={onAppointmentsChange} lang={lang} userId={agent.id} />
            )}

            {/* EDIT AGENT MODAL */}
            {editingAgent && (
              <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,.85)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
                <div style={{ background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 14, padding: "1.5rem", maxWidth: 480, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
                    <div style={{ fontWeight: 800, fontSize: "1rem", color: "var(--c-white,#eef6ff)" }}>{es ? "Editar Perfil" : "Edit Profile"}</div>
                    <button type="button" onClick={() => setEditingAgent(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--c-text3,#45688a)" }}><X size={18} /></button>
                  </div>
                  {[
                    { key: "name", label: es ? "Nombre" : "Name", type: "text" },
                    { key: "email", label: "Email", type: "email" },
                    { key: "phone", label: es ? "Teléfono" : "Phone", type: "tel" },
                    { key: "dob", label: es ? "Fecha de Nacimiento" : "Date of Birth", type: "date" },
                    { key: "address", label: es ? "Dirección" : "Address", type: "text" },
                    { key: "hireDate", label: es ? "Fecha de Ingreso" : "Hire Date", type: "date" },
                    { key: "pin", label: "PIN", type: "text", maxLength: 4 },
                  ].map(field => (
                    <div key={field.key} style={{ marginBottom: ".55rem" }}>
                      <label style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4, fontFamily: "var(--font-mono)" }}>{field.label}</label>
                      <input className="dkt-inp" type={field.type} value={editingAgent[field.key] || ""} maxLength={field.maxLength}
                        onChange={e => setEditingAgent(prev => ({ ...prev, [field.key]: e.target.value }))}
                        style={{ width: "100%", boxSizing: "border-box" }} />
                    </div>
                  ))}
                  <div style={{ marginBottom: ".55rem" }}>
                    <label style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4, fontFamily: "var(--font-mono)" }}>Color</label>
                    <input type="color" className="dkt-inp" value={editingAgent.color || "#06d6a0"} onChange={e => setEditingAgent(prev => ({ ...prev, color: e.target.value }))} style={{ height: 38, padding: 4, width: "100%", boxSizing: "border-box" }} />
                  </div>
                  <div style={{ display: "flex", gap: ".5rem", justifyContent: "flex-end", marginTop: "1rem" }}>
                    <button type="button" onClick={() => setEditingAgent(null)} className="dkt-btn-s">{es ? "Cancelar" : "Cancel"}</button>
                    <button type="button" onClick={() => {
                      onUsersChange(prev => prev.map(u => u.id === editingAgent.id ? { ...u, ...editingAgent, initials: editingAgent.name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) } : u));
                      setEditingAgent(null);
                    }}
                      style={{ display: "flex", alignItems: "center", gap: 5, background: "linear-gradient(135deg,#0099cc,#0055aa)", border: "none", color: "#fff", fontSize: ".78rem", fontWeight: 700, padding: ".5rem 1.2rem", borderRadius: 9, cursor: "pointer", fontFamily: "inherit" }}>
                      <Save size={13} /> {es ? "Guardar" : "Save"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* TEAM */}
      {tab === "team" && (
        <div style={{ display: "flex", flexDirection: "column", gap: ".4rem" }}>
          {(users || []).map(user => {
            const parentAgent = user.parentAgentId ? (users || []).find(u => u.id === user.parentAgentId) : null;
            const roleLabels = { agency_owner: "Agency Owner", agency_admin: "Admin", agent: "Agent", assistant: es ? "Asiento" : "Seat" };
            return (
              <div key={user.id} style={{ background: "var(--c-surface2,#112538)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 10, padding: ".75rem .875rem", display: "flex", alignItems: "center", gap: ".65rem" }}>
                <div style={{ width: 38, height: 38, borderRadius: "50%", background: user.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: ".75rem", fontWeight: 700, color: "#000", border: "2px solid var(--c-border,#1a3550)", flexShrink: 0 }}>{user.initials}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: ".82rem", color: "var(--c-white,#eef6ff)", display: "flex", alignItems: "center", gap: ".4rem" }}>
                    {user.name}
                    <span style={{ fontSize: ".58rem", padding: "1px 6px", borderRadius: 4, background: user.role === "agency_owner" ? "rgba(0,212,255,.12)" : user.role === "agent" ? "rgba(0,255,136,.1)" : "rgba(255,183,0,.1)", color: user.role === "agency_owner" ? "var(--c-cyan,#00d4ff)" : user.role === "agent" ? "var(--c-green,#00ff88)" : "var(--c-amber,#ffb700)", fontWeight: 700 }}>
                      {roleLabels[user.role] || user.role}
                    </span>
                  </div>
                  <div style={{ fontSize: ".65rem", color: "var(--c-text3,#45688a)" }}>
                    PIN: {user.pin}{parentAgent ? ` · ${es ? "Agente" : "Agent"}: ${parentAgent.name}` : ""}
                  </div>
                </div>
                {user.id !== currentUser.id && (
                  <button type="button" onClick={() => removeUser(user.id)} style={{ background: "var(--c-surface3,#162d44)", border: "1px solid var(--c-border,#1a3550)", color: "var(--c-text3,#45688a)", cursor: "pointer", padding: "4px 7px", borderRadius: 7, lineHeight: 0 }}><Trash2 size={12} /></button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* BRANDING — logo upload for client-facing PDFs */}
      {tab === "branding" && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <AgencyNamePanel currentUser={currentUser} lang={lang} userPrefs={userPrefs} onSetPref={onSetPref} />
          <AgencyBrandingPanel currentUser={currentUser} lang={lang} />
        </div>
      )}

      {/* BILLING tab removed — it showed hardcoded placeholder prices that don't
          match Deskte's real pricing and isn't wired to Stripe. Bring it back as a
          real billing panel when live billing ships. */}

      {/* ADD USER MODAL */}
      {addModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,.85)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
          <div style={{ background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 14, padding: "1.5rem", maxWidth: 420, width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
              <div style={{ fontWeight: 800, fontSize: "1rem", color: "var(--c-white,#eef6ff)" }}>{es ? "Agregar Usuario" : "Add User"}</div>
              <button type="button" onClick={() => setAddModal(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--c-text3,#45688a)" }}><X size={18} /></button>
            </div>
            <div style={{ marginBottom: ".5rem" }}>
              <label style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4, fontFamily: "var(--font-mono)" }}>{es ? "Nombre" : "Name"}</label>
              <input className="dkt-inp" value={newAgent.name} onChange={e => setNewAgent(f => ({ ...f, name: e.target.value }))} placeholder="" />
            </div>
            <div style={{ marginBottom: ".5rem" }}>
              <label style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4, fontFamily: "var(--font-mono)" }}>Rol</label>
              <div style={{ display: "flex", gap: ".3rem" }}>
                {[["agent", "Agent", "+$19/mo"], ["assistant", es ? "Asiento" : "Seat", "+$9/mo"], ["agency_admin", "Admin", es ? "Incluido" : "Included"]].map(([role, label, price]) => (
                  <button type="button" key={role} onClick={() => setNewAgent(f => ({ ...f, role, parentAgentId: role === "assistant" ? agents[0]?.id : null }))}
                    style={{ flex: 1, padding: ".45rem", borderRadius: 8, border: `1px solid ${newAgent.role === role ? "rgba(0,212,255,.4)" : "var(--c-border,#1a3550)"}`, background: newAgent.role === role ? "var(--c-cyan-dim)" : "transparent", color: newAgent.role === role ? "var(--c-cyan,#00d4ff)" : "var(--c-text3,#45688a)", fontSize: ".72rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", textAlign: "center" }}>
                    <div>{label}</div><div style={{ fontSize: ".58rem", marginTop: 2, opacity: .7 }}>{price}</div>
                  </button>
                ))}
              </div>
            </div>
            {newAgent.role === "assistant" && (
              <div style={{ marginBottom: ".5rem" }}>
                <label style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4, fontFamily: "var(--font-mono)" }}>{es ? "Asignar a Agente" : "Assign to Agent"}</label>
                <select className="dkt-inp" value={newAgent.parentAgentId || ""} onChange={e => setNewAgent(f => ({ ...f, parentAgentId: e.target.value }))}>
                  {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".5rem", marginBottom: "1rem" }}>
              <div>
                <label style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4, fontFamily: "var(--font-mono)" }}>PIN</label>
                <input className="dkt-inp" value={newAgent.pin} onChange={e => setNewAgent(f => ({ ...f, pin: e.target.value }))} maxLength={4} />
              </div>
              <div>
                <label style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", display: "block", marginBottom: 4, fontFamily: "var(--font-mono)" }}>Color</label>
                <input type="color" className="dkt-inp" value={newAgent.color} onChange={e => setNewAgent(f => ({ ...f, color: e.target.value }))} style={{ height: 38, padding: 4 }} />
              </div>
            </div>
            <button type="button" onClick={addUser} disabled={!newAgent.name}
              style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 5, background: !newAgent.name ? "#2a4560" : "linear-gradient(135deg,#0099cc,#0055aa)", border: "none", color: "#fff", fontSize: ".82rem", fontWeight: 700, padding: ".55rem", borderRadius: 9, cursor: !newAgent.name ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
              <Plus size={14} />{es ? "Agregar" : "Add"} {newAgent.role === "agent" ? "Agent (+$19/mo)" : newAgent.role === "assistant" ? (es ? "Asiento (+$9/mo)" : "Seat (+$9/mo)") : "Admin"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Agency Name panel — lets the OWNER rename the agency ───────────
// The name is set once at signup and shown across the app, on PDFs and
// in outbound email. Only rendered inside AgencyView (owner-gated), and
// the backend re-checks owner/solo on PATCH /agencies/me/name. On save we
// reload so currentUser.agency.name refreshes everywhere it's rendered.
// `userPrefs` / `onSetPref` are optional: when supplied, the panel also hosts
// the font picker for this name, so the typography lives right next to the
// name it styles (Lissette 2026-07-21: "también quiero el editor de letra para
// la parte donde colocamos nombre de la agencia"). This one drives the
// `brandFont` slot — the centered header at the top of the email. The company
// line at the bottom of the signature has its OWN slot, edited in Mi Perfil →
// Compañía ("sí, sepáralo").
export function AgencyNamePanel({ currentUser, lang, userPrefs, onSetPref }) {
  const es = lang === 'es';
  const currentName = currentUser?.agency?.name || '';
  const [name, setName] = useState(currentName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const trimmed = name.trim();
  const dirty = trimmed !== currentName;
  const canSave = !busy && trimmed.length > 0 && trimmed.length <= 120 && dirty;

  const onSave = async () => {
    if (!canSave) return;
    setBusy(true); setError(null); setSuccess(null);
    try {
      await updateAgencyName(trimmed);
      setSuccess(es ? 'Nombre guardado. Recargando…' : 'Name saved. Reloading…');
      // Reload so currentUser.agency.name refreshes everywhere — the
      // sidebar, PDFs and email all read it from /auth/me.
      setTimeout(() => { window.location.reload(); }, 600);
    } catch (err) {
      setError(err.message || String(err));
      setBusy(false);
    }
  };

  return (
    <div style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 14, padding: '1.25rem' }}>
      <div style={{ fontSize: '.7rem', fontWeight: 700, color: 'var(--c-cyan,#00d4ff)', textTransform: 'uppercase', letterSpacing: '.08em', fontFamily: 'var(--font-mono)', marginBottom: '.75rem' }}>
        {es ? 'Nombre de la Agencia' : 'Agency Name'}
      </div>
      <p style={{ fontSize: '.78rem', color: 'var(--c-text2,#7aa3c0)', marginTop: 0, marginBottom: '1rem', lineHeight: 1.5 }}>
        {es
          ? 'Así se llama tu agencia en toda la app, en los PDFs que envías a clientes y en los correos salientes. Solo el dueño puede cambiarlo.'
          : 'This is how your agency is named across the app, on client PDFs, and in outbound email. Only the owner can change it.'}
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem', flexWrap: 'wrap' }}>
        <input
          className="dkt-inp"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={120}
          placeholder={es ? 'Nombre de la agencia' : 'Agency name'}
          style={{ flex: 1, minWidth: 220 }}
          onKeyDown={(e) => { if (e.key === 'Enter') onSave(); }}
        />
        <button type="button" disabled={!canSave} onClick={onSave}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: canSave ? 'linear-gradient(135deg,#0099cc,#0055aa)' : '#2a4560', border: 'none', color: '#fff', fontSize: '.78rem', fontWeight: 700, padding: '.55rem 1rem', borderRadius: 9, cursor: canSave ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}>
          <Save size={14} />{es ? 'Guardar' : 'Save'}
        </button>
      </div>

      {error && (
        <div style={{ marginTop: '.75rem', padding: '.5rem .75rem', background: 'rgba(255,61,107,.08)', border: '1px solid rgba(255,61,107,.25)', borderRadius: 8, fontSize: '.74rem', color: 'var(--c-red,#ff3d6b)' }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ marginTop: '.75rem', padding: '.5rem .75rem', background: 'rgba(0,255,136,.08)', border: '1px solid rgba(0,255,136,.25)', borderRadius: 8, fontSize: '.74rem', color: 'var(--c-green,#00ff88)' }}>
          {success}
        </div>
      )}

      <AgencyTimezone es={es} />

      {onSetPref && (
        <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--c-border,#1a3550)' }}>
          {/* Preview follows the box above as you type, so the agent sees the
              name and its typography together before saving either. */}
          <BrandFontEditor slot="brandFont" prefs={userPrefs} onSetPref={onSetPref} previewName={trimmed || currentName} lang={lang} />
        </div>
      )}
    </div>
  );
}

// ── Agency Branding panel — logo upload for client-facing PDFs ─────
// PNG/JPEG/WebP up to 2MB. The blob URL is persisted on Agency.logoUrl
// and embedded as <img> in quotes/invoices/signature email templates +
// the printed client profile. Lissette 2026-05-27 (final follow-up to
// PR #359 PDF-branding cleanup).
function AgencyBrandingPanel({ currentUser, lang }) {
  const es = lang === 'es';
  // Read the current value from currentUser.agency.logoUrl on mount.
  // Once the upload succeeds, we mirror the new URL into local state so
  // the preview swaps without a full reload (the next /auth/me refresh
  // will overwrite this with the canonical value).
  const [logoUrl, setLogoUrl] = useState(currentUser?.agency?.logoUrl || null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const fileRef = useRef(null);

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setError(null); setSuccess(null);
    try {
      const updated = await uploadAgencyLogo(file);
      setLogoUrl(updated?.logoUrl || null);
      setSuccess(es ? 'Logo guardado. Recargando…' : 'Logo saved. Reloading…');
      // Reload so currentUser.agency.logoUrl refreshes everywhere —
      // print/quote/invoice templates read it. Without reload the
      // in-memory `me` still has the old null logoUrl and prints
      // render without the logo until next login.
      setTimeout(() => { window.location.reload(); }, 600);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const onRemove = async () => {
    if (!(await confirmDialog({ message: es ? '¿Quitar el logo?' : 'Remove the logo?', danger: true }))) return;
    setBusy(true); setError(null); setSuccess(null);
    try {
      await deleteAgencyLogo();
      setLogoUrl(null);
      setSuccess(es ? 'Logo eliminado.' : 'Logo removed.');
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ background: 'var(--c-surface,#0d1c2e)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 14, padding: '1.25rem' }}>
      <div style={{ fontSize: '.7rem', fontWeight: 700, color: 'var(--c-cyan,#00d4ff)', textTransform: 'uppercase', letterSpacing: '.08em', fontFamily: 'var(--font-mono)', marginBottom: '.75rem' }}>
        {es ? 'Logo de la Agencia' : 'Agency Logo'}
      </div>
      <p style={{ fontSize: '.78rem', color: 'var(--c-text2,#7aa3c0)', marginTop: 0, marginBottom: '1rem', lineHeight: 1.5 }}>
        {es
          ? 'Este logo aparece en los PDFs que envías a tus clientes — cotizaciones, facturas, solicitudes de firma y la ficha impresa. Imagen PNG, JPEG o WebP de hasta 2 MB.'
          : 'This logo shows up on PDFs you send to clients — quotes, invoices, signature requests and the printed client profile. PNG, JPEG, or WebP image up to 2 MB.'}
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ width: 280, height: 150, border: '1px dashed var(--c-border2,#1f3f60)', borderRadius: 10, background: 'var(--c-surface2,#112538)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: 12 }}>
          {logoUrl
            ? <img src={logoUrl} alt="Agency logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            : <span style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)', textAlign: 'center', padding: 8 }}><ImageIcon size={20} style={{ display: 'block', margin: '0 auto 4px' }} />{es ? 'Sin logo' : 'No logo'}</span>}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
          <button type="button" disabled={busy} onClick={onPick}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'linear-gradient(135deg,#0099cc,#0055aa)', border: 'none', color: '#fff', fontSize: '.78rem', fontWeight: 700, padding: '.55rem 1rem', borderRadius: 9, cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit', opacity: busy ? .6 : 1 }}>
            <Upload size={14} />{logoUrl ? (es ? 'Reemplazar logo' : 'Replace logo') : (es ? 'Subir logo' : 'Upload logo')}
          </button>
          {logoUrl && (
            <button type="button" disabled={busy} onClick={onRemove}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', border: '1px solid var(--c-red,#ff3d6b)', color: 'var(--c-red,#ff3d6b)', fontSize: '.72rem', fontWeight: 700, padding: '.4rem .85rem', borderRadius: 9, cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
              <Trash2 size={12} />{es ? 'Quitar logo' : 'Remove logo'}
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onFile} style={{ display: 'none' }} />
        </div>
      </div>

      {error && (
        <div style={{ marginTop: '.75rem', padding: '.5rem .75rem', background: 'rgba(255,61,107,.08)', border: '1px solid rgba(255,61,107,.25)', borderRadius: 8, fontSize: '.74rem', color: 'var(--c-red,#ff3d6b)' }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ marginTop: '.75rem', padding: '.5rem .75rem', background: 'rgba(0,255,136,.08)', border: '1px solid rgba(0,255,136,.25)', borderRadius: 8, fontSize: '.74rem', color: 'var(--c-green,#00ff88)' }}>
          {success}
        </div>
      )}

      <div style={{ marginTop: '1rem', padding: '.65rem .85rem', background: 'rgba(0,212,255,.05)', border: '1px solid rgba(0,212,255,.15)', borderRadius: 9, fontSize: '.72rem', color: 'var(--c-text2,#7aa3c0)', lineHeight: 1.5 }}>
        {es ? 'El logo se aplica a partir del próximo PDF que generes. Los PDFs ya enviados quedan con el branding anterior.' : 'The logo applies to PDFs you generate from now on. Already-sent PDFs keep their previous branding.'}
      </div>
    </div>
  );
}

// ── Zona horaria de la agencia ──────────────────────────────────────────
// Existe por el envío programado de los paneles: sin ella, "cada lunes a las 8"
// no tiene significado — 8 ¿de dónde? La lista de zonas la cierra el backend;
// una zona mal escrita mandaría el reporte a una hora impredecible y el error
// solo se notaría cuando el correo llegara tarde.
function AgencyTimezone({ es }) {
  const [tz, setTz] = useState('');
  const [options, setOptions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    getAgencyTimezone()
      .then((d) => { setTz(d.timezone); setOptions(d.options || []); })
      .catch(() => { /* la pantalla sigue siendo util sin esto */ });
  }, []);

  const save = async (next) => {
    setBusy(true);
    setMsg('');
    try {
      await updateAgencyTimezone(next);
      setTz(next);
      setMsg(es ? 'Zona horaria guardada' : 'Timezone saved');
    } catch (e) {
      setMsg(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!options.length) return null;

  return (
    <div style={{ marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid var(--c-border,#1a3550)' }}>
      <div style={{ fontSize: '.7rem', fontWeight: 700, color: 'var(--c-cyan,#00d4ff)', textTransform: 'uppercase', letterSpacing: '.08em', fontFamily: 'var(--font-mono)', marginBottom: '.5rem' }}>
        {es ? 'Zona horaria' : 'Timezone'}
      </div>
      <p style={{ fontSize: '.78rem', color: 'var(--c-text2,#7aa3c0)', marginTop: 0, marginBottom: '.75rem', lineHeight: 1.5 }}>
        {es
          ? 'La hora de tu negocio. Es la que se usa para programar el envio de los paneles por correo: «cada lunes a las 8» significa las 8 aqui.'
          : 'Your business hours. Used to schedule the dashboard email: "every Monday at 8" means 8 here.'}
      </p>
      <select
        className="dkt-inp"
        value={tz}
        disabled={busy}
        onChange={(e) => save(e.target.value)}
        style={{ minWidth: 260 }}
      >
        {options.map((o) => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
      </select>
      {msg && (
        <div style={{ marginTop: '.6rem', fontSize: '.74rem', color: 'var(--c-text2,#7aa3c0)' }}>{msg}</div>
      )}
    </div>
  );
}
